import {
  type VaccineOverride,
  type VaccineOverrideStatus,
  listVaccineOverrides,
  revokeVaccineOverride,
} from '@/lib/api';
import { useCallback, useEffect, useState } from 'react';

const STATUSES: VaccineOverrideStatus[] = ['active', 'expired', 'revoked'];

/**
 * Sub-spec 06 Plan 2 M15 — vaccine override queue.
 *
 * Three-tab list (Active / Expired / Revoked). Per-row: pet name, vaccine
 * name, reason, valid until, used at, granted by, Revoke button (active only).
 *
 * Pet + vaccine names are returned by the GET /vaccine-overrides route which
 * joins pets + vaccines and attaches petName / vaccineName to each row.
 */
export function VaccineOverridesQueuePage() {
  const [status, setStatus] = useState<VaccineOverrideStatus>('active');
  const [overrides, setOverrides] = useState<VaccineOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listVaccineOverrides(status);
      setOverrides(res.overrides);
    } catch (e) {
      setError((e as Error).message ?? 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleRevoke(id: string) {
    if (!confirm('Revoke this override? Future bookings will require the vaccine again.')) return;
    try {
      await revokeVaccineOverride(id);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div className="min-h-[100dvh]">
      <div className="container mx-auto max-w-6xl px-4 py-8">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold">Vaccine overrides</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pets allowed to proceed without a current vaccine on file. Active overrides apply until
            their expiry date or until revoked.
          </p>
        </header>

        <div className="flex gap-1 mb-6 border-b border-border pb-3">
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={[
                'rounded-full px-4 py-1.5 text-sm font-medium transition-colors capitalize',
                status === s
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
              ].join(' ')}
            >
              {s}
            </button>
          ))}
        </div>

        {error && (
          <div className="rounded-lg bg-destructive/10 text-destructive px-4 py-3 mb-4 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : overrides.length === 0 ? (
          <p className="text-muted-foreground text-sm">No {status} overrides.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[640px] w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                <tr>
                  <th className="px-3 py-2">Pet</th>
                  <th className="px-3 py-2">Vaccine</th>
                  <th className="px-3 py-2">Reason</th>
                  <th className="px-3 py-2">Valid until</th>
                  <th className="px-3 py-2">Used at</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {overrides.map((o) => (
                  <tr key={o.id}>
                    <td className="px-3 py-3">{o.petName}</td>
                    <td className="px-3 py-3">{o.vaccineName}</td>
                    <td className="px-3 py-3 max-w-xs truncate" title={o.reason}>
                      {o.reason}
                    </td>
                    <td className="px-3 py-3">{o.validUntil ?? 'Open-ended'}</td>
                    <td className="px-3 py-3 font-mono text-xs">{o.usedAtBookingId ?? '—'}</td>
                    <td className="px-3 py-3 text-right">
                      {status === 'active' && (
                        <button
                          type="button"
                          className="text-xs text-destructive hover:underline"
                          onClick={() => void handleRevoke(o.id)}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
